/**
 * Maps each landing page slug to the contextual HTML mockup + supporting
 * showcase copy that renders between the Offerings and Long-form sections.
 *
 * Visuals are reused across multiple slugs where the story is the same
 * (e.g. fintech & crypto both share `FintechAppVisual`).
 *
 * Per .cursor/rules/app-mockup-visuals.mdc, all imagery here is HTML/CSS;
 * never stock images.
 */

import {
  MobileAppHeroVisual,
  WebAppHeroVisual,
  CustomSoftwareHeroVisual,
  EcommerceHeroVisual,
  AiMlHeroVisual,
  IosAppHeroVisual,
  AndroidAppHeroVisual,
  HybridAppHeroVisual,
} from "../components/HeroVisuals";
import {
  PlatformOpsDashboardVisual,
  CommerceOpsDashboardVisual,
  HealthcareAppVisual,
  ProjectTakeoverVisual,
  FintechAppVisual,
  OnDemandAppVisual,
  RealEstateAppVisual,
  EducationAppVisual,
  DatingAppVisual,
  ArVrAppVisual,
  LocationMapVisual,
  GenAiChatVisual,
  TravelAppVisual,
  InsuranceClaimVisual,
  CostEstimatorVisual,
  StartupProductBoardVisual,
  CrossPlatformStoresVisual,
  MobileDeliveryDashboardVisual,
  AppSupportDeskVisual,
} from "../components/SectionVisuals";
import { locationMapConfigs } from "./locationMapConfigs";

const showcase = (visual, label, heading, description, bullets) => ({
  visual,
  label,
  heading,
  description,
  bullets,
});

// Convenience builder for location landings: pairs LocationMapVisual with
// the city/state-specific map config.
const locationShowcase = (slug, label, heading, description, bullets) => ({
  visual: LocationMapVisual,
  visualProps: { config: locationMapConfigs[slug] },
  label,
  heading,
  description,
  bullets,
});

export const landingShowcases = {
  // ---------- SERVICE LANDING PAGES ----------
  "cross-platform-app-development-company": showcase(
    CrossPlatformStoresVisual,
    "What We Build",
    "One product. Two stores. Native where it earns it.",
    "We design shared architecture so iOS and Android stay on one roadmap, then drop into Swift or Kotlin when a device API would punish a fake.",
    [
      "Stack chosen against the product, not a framework ranking.",
      "Shared screens with platform-native navigation and gestures.",
      "Swift and Kotlin modules for camera, BLE, payments, and ML.",
      "TestFlight and Play Internal on the same merge.",
    ]
  ),
  "react-native-app-development-company": showcase(
    HybridAppHeroVisual,
    "What We Build",
    "One React Native codebase. Two stores. Real native quality.",
    "We design RN apps that feel hand-crafted on iOS and Android, animations at 60fps, native modules where they matter, shared screens where they don't.",
    [
      "TypeScript-first architecture with strict types and reusable hooks.",
      "Native modules in Swift/Kotlin for camera, BLE, and ML workloads.",
      "Reanimated + Gesture Handler for buttery interactions on both stores.",
      "OTA updates via CodePush / Expo for safe, fast rollouts.",
    ]
  ),
  "flutter-app-development-company": showcase(
    HybridAppHeroVisual,
    "What We Build",
    "Pixel-perfect Flutter apps from one Dart codebase.",
    "Flutter lets us deliver high-fidelity UI across iOS, Android, and web from a single codebase, without the usual cross-platform compromises.",
    [
      "Custom widgets with Material You and Cupertino fidelity per platform.",
      "Riverpod / Bloc state management with strict layering and testability.",
      "Platform channels for native iOS/Android integrations.",
      "Flutter Web for shared design systems across mobile and desktop.",
    ]
  ),
  "swift-app-development-company": showcase(
    IosAppHeroVisual,
    "What We Build",
    "Native iOS apps built the Apple way.",
    "We ship iOS apps that feel like they belong on iPhone and iPad, clean SwiftUI architecture, deep integration with Apple's frameworks, and App Store quality from day one.",
    [
      "Swift 6 + SwiftUI with The Composable Architecture or MVVM.",
      "Face ID / Touch ID, Keychain, Sign in with Apple, Apple Pay.",
      "Widgets, App Clips, watchOS companions, and Live Activities.",
      "Xcode Cloud + TestFlight for tight release cycles.",
    ]
  ),
  "kotlin-app-development-company": showcase(
    AndroidAppHeroVisual,
    "What We Build",
    "Native Android apps engineered for the whole device spectrum.",
    "From Pixel phones to rugged field devices, our Kotlin and Jetpack Compose apps look and feel right, and they hold up under real-world conditions.",
    [
      "Kotlin Coroutines + Flow, Hilt DI, and modular Gradle builds.",
      "Jetpack Compose UI with Material You and adaptive layouts.",
      "Offline-first sync with Room, WorkManager, and DataStore.",
      "Play Console staged rollouts, Crashlytics, and Vitals dashboards.",
    ]
  ),
  "ai-app-development-company": showcase(
    AiMlHeroVisual,
    "What We Build",
    "AI applications you can actually ship to users.",
    "We build LLM-powered apps, RAG systems, vision models, and predictive analytics that survive production, with the observability, guardrails, and cost control real products need.",
    [
      "LLM apps with OpenAI, Anthropic, Vertex, and Bedrock.",
      "RAG pipelines on Pinecone, Weaviate, pgvector, or Elastic.",
      "Eval suites, prompt versioning, and per-tenant rate limits.",
      "Inference cost dashboards and fallback / cache strategies.",
    ]
  ),
  "startup-app-development-company": showcase(
    StartupProductBoardVisual,
    "What We Build",
    "A first product you can put in front of customers, not a deck.",
    "We staff senior product engineers who cut scope, ship a real v1, and leave architecture you can grow, weekly demos, not status theater.",
    [
      "Discovery that names the one assumption that has to be true.",
      "Fixed-scope first release with a path to v2, not a rewrite.",
      "Store-ready iOS, Android, and web with analytics from day one.",
      "Weekly working software. A senior engineer you can actually call.",
    ]
  ),
  "mvp-app-development-company": showcase(
    MobileAppHeroVisual,
    "What We Build",
    "An MVP designed to learn, not just to launch.",
    "We help founders ship a focused first version that proves the riskiest assumption, with the analytics and architecture to iterate fast after launch.",
    [
      "Discovery sprint to define the riskiest assumption and KPI.",
      "Lean architecture that scales when the idea works.",
      "Built-in analytics, feature flags, and A/B test instrumentation.",
      "Post-launch retainer to iterate weekly on signal.",
    ]
  ),
  "saas-app-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "Multi-tenant SaaS that grows with your customers.",
    "We design SaaS platforms with usage-based billing, role-based access, audit trails, and the analytics depth your customers expect, production-grade from v1.",
    [
      "Tenant isolation patterns from shared-schema to dedicated DB.",
      "Stripe metered billing, seat-based pricing, and trial flows.",
      "RBAC, SSO (SAML / OIDC), and SCIM provisioning.",
      "Per-tenant dashboards, exports, and usage analytics.",
    ]
  ),
  "mobile-app-backend-erp-integration": showcase(
    CustomSoftwareHeroVisual,
    "What the phone talks to",
    "The record stays in the system you already run.",
    "The app is a window onto the backend or ERP. People finish one job on the phone. The system of record does not move.",
    [
      "One system: SAP, NetSuite, Dynamics, Oracle, or the API you already have.",
      "Company login people already use at the desk.",
      "The last good record stays on screen when the floor has no signal.",
      "A log of who changed the record, and when.",
    ]
  ),
  "ship-mobile-app-mvp": showcase(
    CrossPlatformStoresVisual,
    "What ships",
    "A build people can install on both stores.",
    "Scoped features, a submission, and analytics on whether they finish the job. v1 is the next list, not a rewrite.",
    [
      "One job, written in and out, before a sprint is priced.",
      "TestFlight and Play, then the public listing.",
      "Analytics from the first week: finished, or stopped.",
      "A named path to v1 on the same codebase.",
    ]
  ),
  "enterprise-mobile-app-development-company": showcase(
    CustomSoftwareHeroVisual,
    "What We Build",
    "Enterprise mobile that meets IT, security, and SLA bars.",
    "We deliver enterprise mobile apps that satisfy your CISO, your CIO, and your field teams, secure, observable, and integrated with the systems of record you already run.",
    [
      "SSO via Okta / Azure AD / Ping with MDM (Intune, JAMF) support.",
      "Encrypted-at-rest data, certificate pinning, and zero-trust APIs.",
      "Integrations with SAP, Salesforce, ServiceNow, and Workday.",
      "Compliance-ready: SOC 2, HIPAA, FedRAMP-aligned engineering.",
    ]
  ),
  "custom-mobile-app-development-company": showcase(
    MobileAppHeroVisual,
    "What We Build",
    "Custom mobile apps engineered around your users, not a template.",
    "From the first wireframe to the App Store and Google Play launch, we design and build custom iOS, Android, and cross-platform apps with the UX polish, performance, and reliability real products demand.",
    [
      "Native iOS (Swift) and Android (Kotlin) plus React Native and Flutter.",
      "In-house UI/UX design (research, prototypes, and a real design system).",
      "Secure backends, APIs, push, and real-time sync on AWS, Azure, or GCP.",
      "Senior US-based engineers and two-week sprints with live demos.",
    ]
  ),
  "rebuild-existing-mobile-app": showcase(
    MobileDeliveryDashboardVisual,
    "What we look at",
    "The app you already have, before we touch it.",
    "Crash rate, the framework, and whether a small fix still ships. That is the call between a refresh and a rebuild.",
    [
      "Crash-free sessions and the reviews that name the pain.",
      "The framework, and the last date anyone could change it safely.",
      "The same store listing, so people are not asked to start over.",
      "One slice live. The rest of the old app still in the store.",
    ]
  ),
  "mobile-app-support-and-maintenance": showcase(
    AppSupportDeskVisual,
    "What we keep up",
    "The app stays in the stores after launch day.",
    "Store updates, OS bumps, crash triage, SDK updates, and small features. One name answers when something breaks.",
    [
      "The next iOS and Android build, submitted on your account.",
      "Crash reports read the next morning.",
      "Certificates and SDK updates on a calendar.",
      "Small features that fit. A new product is a separate estimate.",
    ]
  ),

  // ---------- INDUSTRY LANDING PAGES ----------
  "healthcare-app-development-company": showcase(
    HealthcareAppVisual,
    "What We Build",
    "Patient-first apps clinicians and compliance teams trust.",
    "From scheduling to telehealth to remote monitoring, we ship healthcare apps that meet HIPAA, integrate with EHRs, and keep both patients and providers in the loop.",
    [
      "HIPAA-aligned data handling with audit logs and consent flows.",
      "Telehealth video + secure messaging with HL7 / FHIR data.",
      "Remote patient monitoring with HealthKit / Google Fit ingestion.",
      "EHR integrations for Epic, Cerner, Athena, and Allscripts.",
    ]
  ),
  "take-over-mobile-app-project": showcase(
    ProjectTakeoverVisual,
    "What moves first",
    "The code and the store accounts, then a health check.",
    "We do not price a sprint until the repo builds and the App Store and Play accounts are in your name. The next release comes after that read.",
    [
      "Repo admin in the company's account, with history.",
      "App Store and Play invites, not a shared password.",
      "A written health check: build, crashes, signing, last release.",
      "The next fix on the same listing people already have.",
    ]
  ),
  "telemedicine-app-development-company": showcase(
    HealthcareAppVisual,
    "What We Build",
    "Telehealth that feels as good as an in-person visit.",
    "We build virtual care apps with rock-solid video, HIPAA-grade messaging, and the clinical context providers need to treat patients confidently from anywhere.",
    [
      "WebRTC video with adaptive bitrate and waiting-room flows.",
      "Pre-visit intake, e-prescribing, and post-visit summary docs.",
      "Insurance verification and Stripe / Stedi-driven billing.",
      "FHIR / HL7 record sync with major EHR systems.",
    ]
  ),
  "fintech-app-development-company": showcase(
    FintechAppVisual,
    "What We Build",
    "Fintech apps engineered for trust, speed, and scale.",
    "Whether it's banking, investing, lending, or payments, we deliver apps that pass security review, satisfy regulators, and feel as fast as the best consumer products.",
    [
      "PCI-DSS engineering, tokenization, and KYC / AML flows.",
      "ACH, card, and wallet payments via Stripe, Plaid, Marqeta.",
      "Real-time portfolios, alerts, and configurable risk models.",
      "Audit logs, dispute workflows, and chargeback dashboards.",
    ]
  ),
  "crypto-app-development-company": showcase(
    FintechAppVisual,
    "What We Build",
    "Crypto and web3 apps that don't lose your users' coins.",
    "From custodial wallets to DeFi front-ends, we build crypto products with the security posture, observability, and UX that bring the next wave of users in.",
    [
      "Custodial + non-custodial wallets with hardware-key support.",
      "Solana, EVM, and Bitcoin chains with reorg-aware indexing.",
      "On / off-ramps via Circle, MoonPay, and Plaid USD rails.",
      "Smart contract audits, monitoring, and rate-limited RPCs.",
    ]
  ),
  "logistics-app-development-company": showcase(
    AndroidAppHeroVisual,
    "What We Build",
    "Logistics apps that survive the loading dock.",
    "Real conditions (bad signal, gloved hands, rugged devices) drive every design decision. We build logistics apps drivers and dispatchers actually use.",
    [
      "Offline-first sync with conflict resolution and retry queues.",
      "Live GPS, geofencing, and route optimization (Mapbox / Google).",
      "Barcode + RFID scanning on Zebra, Honeywell, and consumer devices.",
      "Dispatcher web console with SLA dashboards and exceptions.",
    ]
  ),
  "on-demand-app-development-company": showcase(
    OnDemandAppVisual,
    "What We Build",
    "On-demand experiences that move in real time.",
    "Riders, customers, and providers all need confidence the platform is working. We build the live tracking, smart matching, and payments that make on-demand feel reliable.",
    [
      "Live ETAs and surge logic with Mapbox / Google Maps.",
      "Three-sided marketplaces (customer / provider / admin) in one stack.",
      "Stripe Connect for instant payouts, tips, and refunds.",
      "Rating, dispute, and trust-and-safety workflows.",
    ]
  ),
  "marketplace-app-development-company": showcase(
    HybridAppHeroVisual,
    "What We Build",
    "Two-sided marketplaces from MVP to liquidity.",
    "We design marketplace apps that solve the cold-start problem with focused supply tools, beautiful buyer experiences, and the trust-and-safety primitives you'll need by month six.",
    [
      "Buyer and seller apps from one cross-platform codebase.",
      "Listing, search, ranking, and reviews built for real catalog scale.",
      "Stripe Connect, escrow, payouts, and dispute flows.",
      "KYC, fraud, and seller onboarding tuned for compliance.",
    ]
  ),
  "iot-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "IoT apps and device fleets you can actually operate.",
    "We build the mobile, web, and cloud layers that turn a pile of connected devices into a managed product, with the telemetry, OTA updates, and alerts your ops team needs.",
    [
      "Device provisioning with AWS IoT Core, Azure IoT Hub, or Greengrass.",
      "MQTT pipelines, time-series storage, and live telemetry dashboards.",
      "OTA firmware rollouts with staged percentages and rollback.",
      "Alerts, SLAs, and per-fleet health scoring in one console.",
    ]
  ),
  "ar-vr-app-development-company": showcase(
    ArVrAppVisual,
    "What We Build",
    "AR and VR experiences engineered for real-world use.",
    "From retail try-on to industrial training, we build immersive apps that ship to App Store, Play Store, Meta Quest, and Vision Pro, not just demos that live in a sandbox.",
    [
      "ARKit + ARCore for plane detection, LiDAR, and persistence.",
      "Unity, Unreal, and WebXR delivery on phones and headsets.",
      "3D asset pipelines via Reality Composer, Blender, and glTF.",
      "Meta Quest, Vision Pro, and HoloLens with Mixed Reality SDKs.",
    ]
  ),
  "fitness-app-development-company": showcase(
    MobileAppHeroVisual,
    "What We Build",
    "Fitness apps users actually open on day 30.",
    "Habits, not features, are what make fitness apps work. We design experiences that reward consistency, sync with the devices users already wear, and feel like a coach in their pocket.",
    [
      "HealthKit and Google Fit ingestion with wearables and rings.",
      "Workouts, programs, and adaptive plans driven by behavior.",
      "Subscription billing (StoreKit + Play Billing) and trials.",
      "Coach + community features with safe messaging and challenges.",
    ]
  ),
  "food-delivery-app-development-company": showcase(
    OnDemandAppVisual,
    "What We Build",
    "Food delivery apps for restaurants, ghost kitchens, and customers.",
    "We ship three-sided delivery platforms with the live tracking, kitchen tooling, and driver dispatch that make orders feel fast, and unit economics work.",
    [
      "Customer app with live ETA, modifiers, and re-order flows.",
      "Restaurant tablet app with order routing and prep timers.",
      "Driver app with optimized routes, stacked orders, and tips.",
      "Stripe Connect payouts plus tax and tipping engine.",
    ]
  ),
  "real-estate-app-development-company": showcase(
    RealEstateAppVisual,
    "What We Build",
    "Real estate apps for buyers, agents, and brokerages.",
    "We integrate MLS data, AR walkthroughs, and lender workflows into apps that feel as polished as Zillow and as data-rich as the back office your agents already use.",
    [
      "MLS ingestion via RESO Web API, Bridge, and Spark.",
      "AR walkthroughs and Matterport / 3D tour playback.",
      "Mortgage calculators, lender hand-offs, and document signing.",
      "Agent CRM, lead routing, and pipeline analytics.",
    ]
  ),
  "education-app-development-company": showcase(
    EducationAppVisual,
    "What We Build",
    "EdTech apps that drive completion, not just sign-ups.",
    "We build learning experiences that respect attention spans, work for K-12 and adult learners, and integrate cleanly with the LMS and SIS systems schools already run.",
    [
      "Adaptive learning paths with mastery checks and streaks.",
      "Live cohorts, breakout rooms, and recorded class playback.",
      "LTI / xAPI / SCORM packaging for LMS integration.",
      "Parent / teacher dashboards and progress reporting.",
    ]
  ),
  "dating-app-development-company": showcase(
    DatingAppVisual,
    "What We Build",
    "Dating apps designed for safety, intent, and real matches.",
    "We build dating products with verified profiles, healthy moderation, and matching that doesn't just chase engagement (features that protect your community as it grows).",
    [
      "ID + photo verification with smart liveness checks.",
      "Matching ranked on signals beyond swipes (intent, replies, dates).",
      "Block, report, and abuse-detection workflows.",
      "Stripe / StoreKit subscriptions, boosts, and gifts.",
    ]
  ),
  "ecommerce-app-development-company": showcase(
    EcommerceHeroVisual,
    "What We Build",
    "Commerce apps that convert across every channel.",
    "We build headless storefronts, native commerce apps, and merchant tooling that share inventory, pricing, and customer data, so every channel sells.",
    [
      "Headless Shopify / commercetools / BigCommerce front-ends.",
      "Native iOS / Android shopping with Apple Pay and Google Pay.",
      "Subscription, B2B, and wholesale flows from the same backend.",
      "Real-time inventory, fraud screening, and conversion analytics.",
    ]
  ),

  // ---------- LOCATION LANDING PAGES ----------
  // Each one pairs the showcase copy with a stylized, animated map of the
  // metro: HQ pin, client pins, dashed delivery routes, district labels,
  // and a side panel of recent local engagements.
  "app-development-company-washington-dc": locationShowcase(
    "app-development-company-washington-dc",
    "On the Ground in the DMV",
    "DC, Virginia, and Maryland teams (built on the ground).",
    "We've spent decades shipping apps for federal agencies, government-services contractors, and Beltway-area enterprises. Local availability, cleared engineers when you need them, and a real understanding of the regulatory environment.",
    [
      "On-site collaboration across DC, NoVA, and the Maryland metro.",
      "Experience with FedRAMP / FISMA / HIPAA-grade engineering.",
      "Senior US-based engineers, never offshored coordination overhead.",
      "Local partnership history with AT&T Government Solutions, FiscalNote, and more.",
    ]
  ),
  "app-development-company-virginia": locationShowcase(
    "app-development-company-virginia",
    "On the Ground in Virginia",
    "Senior app teams across Virginia (Reston to Richmond).",
    "From DC suburbs to Hampton Roads, we partner with Virginia-based startups, defense contractors, and Fortune 500s to deliver apps with the rigor enterprise stakeholders expect.",
    [
      "On-site presence across Northern Virginia and Richmond.",
      "Cleared and clearable engineers for sensitive workloads.",
      "Compliance fluency (FedRAMP, HIPAA, SOC 2, PCI-DSS).",
      "Strong local network across CFOs, CISOs, and product leaders.",
    ]
  ),
  "app-development-company-maryland": locationShowcase(
    "app-development-company-maryland",
    "On the Ground in Maryland",
    "Maryland-based app teams that ship.",
    "We work shoulder-to-shoulder with Maryland businesses, NIH-adjacent biotech, and Beltway enterprises, combining deep technical depth with the local accountability you can't get from a remote-only shop.",
    [
      "On-site work across Bethesda, Columbia, Baltimore, and Frederick.",
      "Healthcare + life-sciences engineering experience (HIPAA, HITRUST).",
      "Senior, US-based engineers with deep mobile and web expertise.",
      "Track record of public + private sector delivery.",
    ]
  ),
  "app-development-company-new-york": locationShowcase(
    "app-development-company-new-york",
    "On the Ground in NYC",
    "New York-grade app teams that move at NYC speed.",
    "We work with NYC startups, agencies, and enterprises to ship fintech, media, and consumer apps with senior engineering and the velocity New York demands.",
    [
      "On-site presence across Manhattan, Brooklyn, and Long Island City.",
      "Fintech, media, and consumer app experience at scale.",
      "Senior, US-based engineers with NYC business fluency.",
      "Track record of partnering with Fortune 500s headquartered in NYC.",
    ]
  ),
  "app-development-company-boston": locationShowcase(
    "app-development-company-boston",
    "On the Ground in Greater Boston",
    "Boston-area engineering, biotech-fluent.",
    "From Kendall Square biotech to Cambridge AI labs, we ship apps that respect the regulatory, scientific, and academic environments unique to Boston.",
    [
      "On-site collaboration across Boston, Cambridge, and Waltham.",
      "Healthcare, biotech, and EdTech engineering experience.",
      "HIPAA, HITRUST, and FERPA-aligned engineering.",
      "Senior US-based engineers, never outsourced.",
    ]
  ),
  "app-development-company-austin": locationShowcase(
    "app-development-company-austin",
    "On the Ground in Austin",
    "Austin-based app teams for Texas-scale ambition.",
    "We partner with Austin startups, Tesla-adjacent suppliers, and enterprise SaaS companies to ship apps with the speed and senior expertise the Texas tech corridor demands.",
    [
      "On-site presence in Austin and across the I-35 corridor.",
      "SaaS, fintech, and IoT engineering experience.",
      "Senior, US-based engineers, no offshore handoffs.",
      "Local network of CTOs, founders, and product leaders.",
    ]
  ),
  "app-development-company-san-francisco": locationShowcase(
    "app-development-company-san-francisco",
    "On the Ground in the Bay Area",
    "SF and Peninsula app teams for the bar Bay Area buyers set.",
    "From SoMa SaaS to Peninsula AI startups, we ship apps with the engineering depth and product polish Bay Area customers, investors, and engineers expect.",
    [
      "On-site collaboration across SoMa, the Peninsula, and the East Bay.",
      "Generative AI, SaaS, and fintech engineering depth.",
      "Senior US-based engineers fluent in Bay Area stacks.",
      "PT-aligned hours and Bay Area-grade craft.",
    ]
  ),
  "app-development-company-chicago": locationShowcase(
    "app-development-company-chicago",
    "On the Ground in Chicago",
    "Loop and Fulton Market teams for B2B, insurance, and logistics.",
    "From Loop carriers to Fulton Market B2B SaaS, we ship apps with the engineering rigor and operational pragmatism Midwest customers expect.",
    [
      "On-site collaboration across the Loop, River North, and Fulton Market.",
      "Insurance, logistics, manufacturing, and B2B fluency.",
      "Senior US-based engineers familiar with carrier-grade systems.",
      "Central Time alignment and Midwest-grade reliability.",
    ]
  ),
  "app-development-company-los-angeles": locationShowcase(
    "app-development-company-los-angeles",
    "On the Ground in LA",
    "LA-area app teams for entertainment, commerce, and beyond.",
    "From media-tech in Santa Monica to commerce in DTLA, we ship apps with the craftsmanship LA brands demand and the engineering rigor enterprise platforms require.",
    [
      "On-site collaboration across Santa Monica, DTLA, and the West Side.",
      "Media, entertainment, and direct-to-consumer expertise.",
      "Senior US-based engineers with creative-industry fluency.",
      "Track record with BMW Performance Center and Microsoft-affiliated teams.",
    ]
  ),

  // ---------- THIRD BATCH (services) ----------
  "web-app-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "Web apps that load fast, scale clean, and feel polished from first login.",
    "From customer portals to multi-tenant SaaS to internal admin tools, we ship web applications with strict typing, accessibility built in, and the observability your team needs to operate them confidently.",
    [
      "TypeScript-first React / Next.js with App Router and RSC where it fits.",
      "Tailwind design systems with WCAG 2.2 AA accessibility from day one.",
      "GraphQL / REST APIs on Node.js, Python, or .NET. Your choice.",
      "PostgreSQL + Redis foundations with CI/CD and observability baked in.",
    ]
  ),
  "custom-software-development-company": showcase(
    CustomSoftwareHeroVisual,
    "What We Build",
    "Custom software designed around your workflows, not the framework's.",
    "We build bespoke platforms, internal tools, and integrations for Fortune 500s and funded startups, clean architecture, modular boundaries, and runbooks your team can take over.",
    [
      "Workflow + process automation for approvals, intake, and document handling.",
      "Integrations with Salesforce, SAP, NetSuite, Workday, and ServiceNow.",
      "Legacy modernization from .NET / Java / PHP into cloud-native architectures.",
      "AWS / GCP / Azure with IaC, observability, and zero-downtime deploys.",
    ]
  ),
  "generative-ai-development-company": showcase(
    GenAiChatVisual,
    "What We Build",
    "Generative AI products that survive production, not just demos.",
    "We design LLM apps, RAG systems, and agents with the eval suites, guardrails, and cost control real products need, so your AI feature behaves the same on Tuesday as it did at the launch demo.",
    [
      "LLM apps on OpenAI, Anthropic, Bedrock, Vertex, or open-source models.",
      "RAG with Pinecone, Weaviate, pgvector (chunking + re-ranking that works).",
      "Eval suites, prompt versioning, content filters, and red-team testing.",
      "Model routing, caching, and per-tenant rate limits for cost control.",
    ]
  ),
  "progressive-web-app-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "Installable, offline-first PWAs that feel native everywhere.",
    "We design progressive web apps with service workers, IndexedDB caching, web push, and Core Web Vitals tuned for store-grade UX, so users add to home screen and keep them.",
    [
      "Service workers, IndexedDB, and background sync for offline-first UX.",
      "Web push on Chrome, Edge, Firefox, and modern Safari.",
      "App manifests + add-to-home-screen funnels with install analytics.",
      "Sub-second loads and 60fps interactions across desktop and mobile.",
    ]
  ),

  // ---------- THIRD BATCH (industries) ----------
  "travel-app-development-company": showcase(
    TravelAppVisual,
    "What We Build",
    "Travel apps people actually open during the trip.",
    "Booking, itineraries, boarding passes, and loyalty done right, engineered for the offline-first, disruption-heavy reality of how travelers actually use their phones.",
    [
      "Sabre / Amadeus / Travelport plus direct supplier API integrations.",
      "Offline-first itineraries with Apple Wallet + Google Wallet sync.",
      "Push, SMS, and in-app messaging for gate changes and disruptions.",
      "Loyalty programs with points, tiers, and partner redemption.",
    ]
  ),
  "insurance-app-development-company": showcase(
    InsuranceClaimVisual,
    "What We Build",
    "Insurance software that moves the carrier, not just the website.",
    "Policy administration, claims processing, agent tooling, and customer experience platforms designed with the regulatory, audit, and core-system realities carriers operate in.",
    [
      "Guidewire, Duck Creek, and Majesco core-system integrations.",
      "FNOL intake, AI photo triage, and adjuster workflow tooling.",
      "Rating engines, document generation, and state-by-state DOI alignment.",
      "Producer portals with quoting, book of business, and commissions.",
    ]
  ),
  "government-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Public-sector apps with FedRAMP-aligned security and Section 508 accessibility.",
    "We design citizen-facing portals, agency workforce tools, and modernization replacements with the security posture, audit trail, and accessibility public-sector buyers require.",
    [
      "FedRAMP Moderate / High control alignment on GovCloud, GCC High, Azure Gov.",
      "Section 508 + WCAG 2.2 AA accessibility tested with real assistive tech.",
      "ATO documentation, NIST 800-53 controls, and continuous monitoring.",
      "DMV-based senior engineers, cleared engagements available.",
    ]
  ),

  // ---------- BATCH 4 (industries) ----------
  "automotive-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Connected vehicles, fleets, and dealership experiences that hold up in the real world.",
    "We architect companion apps, telematics dashboards, and owner journeys with offline-first patterns, secure device binding, and integrations that survive flaky networks and long vehicle lifetimes.",
    [
      "Telemetry ingestion with graceful degradation on low signal and parking-garage dead zones.",
      "Phone-as-key and charging flows with fraud signals and PCI-aware payment patterns.",
      "Fleet ops: DVIR, Geotab-style telematics hooks, Hours of Service, and geofenced workflows.",
      "MQTT / REST / webhook bridges to your existing telematics and CRM stack.",
    ]
  ),
  "retail-app-development-company": showcase(
    CommerceOpsDashboardVisual,
    "What We Build",
    "Store, associate, and fulfillment apps that match your real inventory graph.",
    "From clienteling tablets to BOPIS and RFID cycle counts, we ship retail mobile that reconciles fast under peak traffic, not CRUD painted over broken OMS truth.",
    [
      "Offline-capable scanning and associate workflows with deterministic conflict surfacing.",
      "Payments and vaulted tokens aligned to acquirer and PF security questionnaires.",
      "RFID and cycle-count exception flows that supervisors can clear without spreadsheets.",
      "Headless commerce + edge caching patterns that keep lists fast on the floor.",
    ]
  ),
  "hospitality-app-development-company": showcase(
    TravelAppVisual,
    "What We Build",
    "Guest-first hotel and restaurant apps that earn tips and five-star reviews.",
    "Check-in, digital keys, F&B, spa booking, and loyalty, engineered for noisy Wi‑Fi, multilingual guests, and franchise owners who need one consistent brand experience.",
    [
      "PMS / CRS integration patterns with idempotent webhooks and resilient caching.",
      "Digital key flows with fallback paths when readers or BLE stacks misbehave.",
      "PCI scope reduction through network tokens and delegated fields on staff devices.",
      "Housekeeping and ops boards that compress signal for busy GMs.",
    ]
  ),
  "legal-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Legal workflow software with privilege-aware access and evidence-grade logging.",
    "Matter intake, document lifecycles, outside counsel analytics, and mobile capture, designed so ethical walls, retention, and export packs hold up when it matters.",
    [
      "Hierarchical RBAC with break-glass flows and dual-control overrides.",
      "OCR, redaction, and Bates workflows with immutable audit trails.",
      "Mobile capture with encrypted background sync and secure viewers.",
      "Exports aligned to e-discovery tooling and custodial hold requirements.",
    ]
  ),
  "media-entertainment-app-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "Streaming and publishing apps built for rights, scale, and sponsor reality.",
    "Playback, DRM ladders, blackout logic, editorial velocity, and monetization, with QoE telemetry that separates hype glitches from real outages.",
    [
      "Adaptive streaming with offline packs, entitlement refresh, and CDN cost guardrails.",
      "DRM (Widevine / FairPlay) routing behind testable abstraction layers.",
      "SVOD / AVOD / TVOD monetization modules with honest ledger boundaries.",
      "Personalization and AI summaries with human review and rollback-friendly flags.",
    ]
  ),
  "social-network-app-development-company": showcase(
    HybridAppHeroVisual,
    "What We Build",
    "Social graphs, chat, and creator payouts engineered for safety and scale.",
    "Feeds, live rooms, moderation queues, and monetization, with abuse budgets, rate limits, and latency discipline that keep communities healthy as they grow.",
    [
      "Ranking and cold-start strategies with replayable evaluation snapshots.",
      "Realtime messaging and presence with backoff sync for flaky networks.",
      "Trust & safety workflows with human escalation and evidentiary logging.",
      "Creator earnings (subs, tips, rev-share) with chargeback-conscious accounting.",
    ]
  ),
  "sports-app-development-company": showcase(
    MobileAppHeroVisual,
    "What We Build",
    "Gameday apps fans open when the whistle blows.",
    "Live scores, venue modes, ticketing companions, concessions, loyalty, and sponsor surfaces, tolerant to stadium congestion and blackout rules that change by market.",
    [
      "Edge-friendly score deltas with blackout-safe delay contracts.",
      "Wallet passes, resale policy surfacing, and ADA-aware venue navigation.",
      "Geofenced sponsor offers without invasive tracking shortcuts.",
      "Burst traffic patterns rehearsed before opening night.",
    ]
  ),
  "agriculture-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Field apps scouts and crews trust when connectivity disappears.",
    "Offline GIS plots, telemetry from mixed machinery, harvest logistics queues, cooperative ERP bridges, merges that surface conflicts honestly instead of hiding them.",
    [
      "ISOXML-informed ingestion patterns abstracted across OEM quirks.",
      "Conflict-visible offline queues tuned for agronomic correctness.",
      "Trace exports where auditors require lineage, gated by counsel.",
      "Sensor and irrigation scheduling with drift-aware calibration handling.",
    ]
  ),
  "construction-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Jobsite apps supers actually open with gloves on.",
    "Daily reports, punch lists pinned to drawings, safety attestations, and delivery coordination, resilient to dust, glare, basement dead zones, and subs who already get too many notifications.",
    [
      "Drawing version arbitration, no silent overwrites on contested sheets.",
      "Photo metadata chains suited for incident and QA reviews.",
      "Procore / Autodesk-style integrations with idempotent webhook + nightly reconciliation.",
      "Geofenced site modes for equipment and checklist context switching.",
    ]
  ),
  "event-app-development-company": showcase(
    OnDemandAppVisual,
    "What We Build",
    "Conference and venue apps that survive badge-scan peaks.",
    "Agendas, networking, live Q&A, exhibitor lead capture, and sponsor analytics, load-tested for opening keynotes and honest degraded modes when Wi‑Fi gives up.",
    [
      "Ticketing fallbacks + anti-fraud velocity checks at scan gates.",
      "Moderation-friendly Q&A with bot-resistant upvote fairness patterns.",
      "Sponsor ROI dashboards that stay honest to contract definitions.",
      "Pre-synced itineraries and maps for flaky conference networks.",
    ]
  ),

  // ---------- THIRD BATCH (campaign) ----------
  "free-app-cost-estimate": showcase(
    CostEstimatorVisual,
    "What You Get",
    "A real cost range, reviewed by a senior engineer, in under 3 minutes.",
    "Most online app cost calculators produce uselessly broad ranges or quietly anchored sales numbers. Ours is calibrated against work we've shipped since 2002 and reviewed by a senior engineer before it lands in your inbox.",
    [
      "Low / expected / high range tuned to your actual scope and platforms.",
      "A line-item breakdown of what drives the number, no black box.",
      "Recommended phasing (MVP vs full v1) to match budget and timeline.",
      "No automated drip sequence. One useful number, then we step back.",
    ]
  ),

  // ---------- BATCH 6 (services) ----------
  "dotnet-app-development-company": showcase(
    CustomSoftwareHeroVisual,
    "What We Build",
    "Modern .NET platforms that scale and stay maintainable.",
    "We build enterprise apps on modern .NET (clean architecture, typed APIs, and Azure-native delivery) so your team inherits software it can actually own and evolve.",
    [
      "ASP.NET Core APIs with clean architecture and EF Core.",
      "Blazor and SPA front-ends backed by typed .NET services.",
      "Azure App Service, Functions, and AKS with IaC and CI/CD.",
      "Incremental migrations off legacy .NET Framework and WCF.",
    ]
  ),
  "cloud-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Cloud-native apps that scale on purpose, not by surprise.",
    "We design for the cloud's strengths: managed services, horizontal scale, infrastructure-as-code, and graceful failure, with the observability and cost controls production needs.",
    [
      "Serverless and container workloads on AWS, Azure, and GCP.",
      "Kubernetes (EKS / AKS / GKE) with sane autoscaling and zero-downtime deploys.",
      "Terraform / CDK / Bicep so environments are reproducible and reviewable.",
      "FinOps dashboards and right-sizing that keep the bill honest.",
    ]
  ),
  "blockchain-app-development-company": showcase(
    FintechAppVisual,
    "What We Build",
    "Web3 products where security is the feature.",
    "From audited smart contracts to wallet-connected dApps, we build blockchain products with the test coverage, monitoring, and mainstream UX that real adoption requires.",
    [
      "Audited Solidity / Rust contracts with fuzzing and full test coverage.",
      "EVM, Solana, and L2 integration with reorg-aware indexing.",
      "Gas and account abstraction plus fiat on-ramps for normal users.",
      "On-chain monitoring, alerting, and rate-limited RPC layers.",
    ]
  ),

  // ---------- BATCH 6 (industries) ----------
  "banking-app-development-company": showcase(
    FintechAppVisual,
    "What We Build",
    "Banking apps customers trust and regulators approve.",
    "We deliver mobile and digital banking experiences with the security posture your CISO requires and the polish customers expect from a challenger bank.",
    [
      "Digital onboarding with KYC/AML and document capture.",
      "ACH, RTP, Zelle, and card issuance with tokenized handling.",
      "Core and BaaS integration (FIS, Fiserv, Jack Henry, modern APIs).",
      "Fraud detection, audit trails, and SOC 2 / PCI-DSS alignment.",
    ]
  ),
  "manufacturing-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Software that survives the factory floor.",
    "MES tooling, shop-floor apps, and IoT dashboards engineered for rugged devices, spotty networks, and operators who can't stop the line to fight with software.",
    [
      "Offline-first work orders, traceability, and digital work instructions.",
      "Real-time OEE, downtime, and quality dashboards.",
      "PLC / SCADA / sensor data via OPC-UA and MQTT.",
      "ERP, MES, and QMS integration (SAP, Oracle, NetSuite).",
    ]
  ),
  "gaming-app-development-company": showcase(
    ArVrAppVisual,
    "What We Build",
    "Games engineered for retention, not just launch day.",
    "We build mobile and cross-platform games in Unity and Unreal with the netcode, economy design, and LiveOps infrastructure that turn a launch into a long-running hit.",
    [
      "60fps Unity / Unreal builds tuned for mid-range devices.",
      "Authoritative-server multiplayer with matchmaking and anti-cheat.",
      "IAP, ads, and economy design that earn without burning players.",
      "LiveOps content pipelines, remote config, and A/B testing.",
    ]
  ),
  "mental-health-app-development-company": showcase(
    HealthcareAppVisual,
    "What We Build",
    "Mental health apps that are safe, engaging, and effective.",
    "We build teletherapy, wellness, and digital therapeutic apps with HIPAA-aware security, crisis-aware safeguards, and the warm, low-friction UX that keeps users in their care.",
    [
      "HIPAA-aware storage, consent flows, and audit trails.",
      "Teletherapy video, scheduling, and secure messaging.",
      "Evidence-based CBT/DBT programs and mood tracking.",
      "Clinician dashboards and measurement-based outcomes.",
    ]
  ),

  // ---------- BATCH 6 (locations) ----------
  "app-development-company-dallas": locationShowcase(
    "app-development-company-dallas",
    "On the Ground in DFW",
    "Dallas–Fort Worth teams built for Metroplex scale.",
    "We partner with DFW fintech, logistics, healthcare, and enterprise headquarters, meeting enterprise expectations on security and integration while moving at Texas speed.",
    [
      "On-site collaboration across Downtown, Uptown, Plano, and Fort Worth.",
      "Fintech (PCI-DSS) and logistics engineering depth.",
      "Enterprise integration: ERP, CRM, identity, and telecom systems.",
      "Central Time alignment for standups and executive demos.",
    ]
  ),
  "app-development-company-atlanta": locationShowcase(
    "app-development-company-atlanta",
    "On the Ground in Atlanta",
    "Atlanta teams fluent in payments and logistics.",
    "From 'Transaction Alley' fintech to the world's busiest airport's logistics network, we ship apps with the payments depth and supply-chain fluency metro Atlanta runs on.",
    [
      "On-site collaboration across Midtown, Buckhead, and Alpharetta.",
      "PCI-aware payments and processing engineering.",
      "Logistics and supply-chain patterns for the Southeast hub.",
      "Eastern Time alignment and startup-friendly velocity.",
    ]
  ),
  "app-development-company-phoenix": locationShowcase(
    "app-development-company-phoenix",
    "On the Ground in the Valley",
    "Phoenix teams for one of the fastest-growing metros.",
    "We partner with Valley fintech back offices, health networks, semiconductor and manufacturing employers, and startups, pragmatic execution that scales as the region grows.",
    [
      "On-site collaboration across Downtown, Scottsdale, Tempe, and Chandler.",
      "Financial-services (PCI / SOC 2) and healthcare (HIPAA) fluency.",
      "Operations and field tools for manufacturing and logistics.",
      "Mountain Time alignment for standups and milestones.",
    ]
  ),

  // ---------- BATCH 7 ----------
  "ai-agent-development-company": showcase(
    GenAiChatVisual,
    "What We Build",
    "AI agents that do real work (safely and observably).",
    "We build autonomous and multi-agent systems with the tool integrations, guardrails, and eval suites production agents require, so they behave the same on run 1,000 as on run one.",
    [
      "Autonomous task agents with human-in-the-loop checkpoints.",
      "Multi-agent orchestration via LangGraph, CrewAI, or custom graphs.",
      "Tool + system integration through function calling and MCP.",
      "Evals, tracing, guardrails, and cost caps built in from day one.",
    ]
  ),
  "energy-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Energy software for the field, the grid, and the regulator.",
    "Grid ops, asset monitoring, and field-service apps engineered for remote sites, legacy SCADA, safety-sensitive work, and the auditability critical infrastructure demands.",
    [
      "Offline-first field ops for substations, well sites, and remote assets.",
      "SCADA / DERMS / IoT integration via OPC-UA and MQTT.",
      "Real-time telemetry, thresholds, and predictive maintenance.",
      "NERC CIP-aware security, encryption, and audit trails.",
    ]
  ),
  "app-development-company-houston": locationShowcase(
    "app-development-company-houston",
    "On the Ground in Houston",
    "Houston teams fluent in energy, health, and trade.",
    "From the Energy Corridor to the Texas Medical Center to the Port of Houston, we ship apps with the SCADA, HIPAA, and logistics depth the Gulf Coast's industries run on.",
    [
      "On-site collaboration across Downtown, the Med Center, and the Energy Corridor.",
      "Energy and oil & gas field-ops and SCADA/IoT fluency.",
      "HIPAA-aware apps for the Texas Medical Center ecosystem.",
      "Central Time alignment and Port-adjacent logistics experience.",
    ]
  ),

  // ---------- BATCH 8 (services) ----------
  "python-app-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "Python platforms that are versatile and production-grade.",
    "Web apps, APIs, data pipelines, and AI systems on Python, typed, tested, and observable, so what starts as a prototype becomes something you can operate for years.",
    [
      "Django and FastAPI web apps with clean architecture and strong tests.",
      "Async APIs, Celery workers, and event-driven microservices.",
      "ETL/ELT and data pipelines with Airflow and pandas.",
      "ML and LLM systems wired into real apps with evals and guardrails.",
    ]
  ),
  "chatbot-development-company": showcase(
    GenAiChatVisual,
    "What We Build",
    "Chatbots that resolve requests, not just deflect them.",
    "We ground assistants in your real content with RAG, connect them to your systems so they can take action, and wrap them in guardrails that keep answers honest.",
    [
      "RAG grounding over your docs and databases with citations.",
      "CRM, helpdesk, and API integration so the bot actually acts.",
      "Web, WhatsApp, Slack, Teams, SMS, and voice channels.",
      "Content filters, human handoff, and conversation analytics.",
    ]
  ),
  "low-code-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Low-code speed with real governance and no lock-in.",
    "We ship internal tools and workflow apps in weeks on the right platform, then add the integrations, security, and pro-code escape hatches low-code usually lacks.",
    [
      "Internal tools on Retool and Budibase in weeks, not quarters.",
      "Workflow apps on Power Platform, OutSystems, or Mendix.",
      "Hybrid low-code + pro-code so you're never trapped.",
      "RBAC, audit logs, environments, and review gates for safety.",
    ]
  ),

  // ---------- BATCH 8 (industries) ----------
  "taxi-app-development-company": showcase(
    OnDemandAppVisual,
    "What We Build",
    "Ride-hailing platforms that feel reliable from ride one.",
    "Rider, driver, and dispatch apps with the live tracking, smart matching, and reliable payments that make on-demand transport actually work.",
    [
      "Rider and driver apps with live ETAs, fares, and ratings.",
      "Real-time GPS, routing, matching, and surge logic.",
      "Stripe Connect payments, tips, and instant driver payouts.",
      "Driver verification, SOS, and trip sharing for safety.",
    ]
  ),
  "grocery-app-development-company": showcase(
    CommerceOpsDashboardVisual,
    "What We Build",
    "Grocery commerce grounded in real, live inventory.",
    "Customer, shopper, and store apps built around accurate inventory, with substitutions, delivery slots, batching, and grocery-specific payments.",
    [
      "Real-time stock and pricing synced with your POS/ERP.",
      "Shopper picking, scanning, and substitution flows.",
      "Delivery slots, batching, and route optimization with cold-chain awareness.",
      "Weight-based pricing, coupons, loyalty, and EBT-aware payments.",
    ]
  ),
  "hr-app-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "HR software people actually want to open.",
    "Recruiting, HRIS, onboarding, and employee-experience apps that are modern and mobile-friendly, and secure and integrated with the systems you already run.",
    [
      "ATS, HRIS, onboarding, time, and performance in one stack.",
      "Integrations with Workday, ADP, BambooHR, and Gusto.",
      "SSO and SCIM provisioning for clean access management.",
      "Encryption, RBAC, and GDPR/EEOC-aware data handling.",
    ]
  ),
  "music-app-development-company": showcase(
    MobileAppHeroVisual,
    "What We Build",
    "Music apps engineered for playback and rights.",
    "Streaming, live-audio, and creation apps with the low-latency, offline-capable playback users demand and the licensing-aware architecture rights holders require.",
    [
      "Adaptive, gapless, low-latency playback with offline downloads.",
      "Live audio, radio, and rooms with resilient buffering.",
      "DRM, geo-restrictions, and PRO/label-aligned reporting.",
      "Subscriptions, ad tiers, tipping, and artist payouts.",
    ]
  ),

  // ---------- BATCH 8 (locations) ----------
  "app-development-company-san-diego": locationShowcase(
    "app-development-company-san-diego",
    "On the Ground in San Diego",
    "San Diego teams fluent in biotech, defense, and health.",
    "From the Sorrento Valley biotech cluster to defense and maritime programs to digital health, we ship apps with the research rigor, security, and polish SoCal buyers expect.",
    [
      "On-site collaboration across Downtown, La Jolla, and Sorrento Valley.",
      "Biotech, genomics, and HIPAA/HITRUST-aware engineering.",
      "Defense- and maritime-grade security fluency.",
      "Pacific Time alignment and startup-friendly velocity.",
    ]
  ),
  "app-development-company-philadelphia": locationShowcase(
    "app-development-company-philadelphia",
    "On the Ground in Philadelphia",
    "Philadelphia teams for health, education, and enterprise.",
    "From world-class health systems to one of the densest university clusters in the country, we ship apps with the HIPAA, academic, and compliance depth the Delaware Valley runs on.",
    [
      "On-site collaboration across Center City and University City.",
      "Healthcare, pharma, and HIPAA/HITRUST-aware engineering.",
      "Higher-ed and LMS-integrated app experience.",
      "Eastern Time alignment and fintech/insurance compliance fluency.",
    ]
  ),
  "app-development-company-nashville": locationShowcase(
    "app-development-company-nashville",
    "On the Ground in Nashville",
    "Nashville teams fluent in health services and music.",
    "As healthcare's HQ and the home of the music business, Nashville demands both HIPAA/RCM depth and licensing-aware entertainment engineering, and we bring both.",
    [
      "On-site collaboration across Downtown, The Gulch, and Music Row.",
      "Healthcare-services, RCM, and HIPAA-aware engineering.",
      "Music-industry streaming, royalty, and rights fluency.",
      "Central Time alignment and startup-friendly velocity.",
    ]
  ),

  // ---------- BATCH 9 (services) ----------
  "nextjs-app-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "Next.js apps that are fast, SEO-ready, and maintainable.",
    "SaaS products, storefronts, and full-stack web apps on the App Router, with rendering strategies tuned for Core Web Vitals and React Server Components done right.",
    [
      "App Router and React Server Components by default for new builds.",
      "SSR, SSG, ISR, and edge rendering tuned for SEO and speed.",
      "Typed data layers with Prisma/Drizzle and clean API routes.",
      "Vercel, AWS, or Azure deploys with previews and observability.",
    ]
  ),
  "nodejs-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Node.js backends that hold up under real load.",
    "APIs, real-time systems, and microservices on Express and NestJS. TypeScript-first, queued for heavy work, and observable in production.",
    [
      "REST and GraphQL APIs with NestJS or lean Express services.",
      "WebSockets and event streams for chat, collab, and live dashboards.",
      "BullMQ/Redis workers for reliable async processing.",
      "Auth, rate limits, tracing, and cloud autoscaling built in.",
    ]
  ),
  "laravel-app-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "Laravel apps that stay elegant as you scale.",
    "Web platforms, APIs, and admin systems on Laravel (clean domains, Horizon queues, and cloud deploys that keep PHP products maintainable long after launch).",
    [
      "Full-stack Laravel with Blade, Livewire, or Inertia/Vue/React.",
      "Sanctum/Passport APIs with clear resource contracts.",
      "Filament, Nova, or custom admin panels ops teams enjoy.",
      "Forge, Vapor, or containerized Laravel with CI/CD and monitoring.",
    ]
  ),

  // ---------- BATCH 9 (industries) ----------
  "restaurant-app-development-company": showcase(
    OnDemandAppVisual,
    "What We Build",
    "Restaurant apps that survive the dinner rush.",
    "Online ordering, loyalty, POS, and kitchen-display tools built for speed on the floor, with POS and delivery integrations that keep tickets accurate.",
    [
      "Branded ordering and menus with mods and real-time availability.",
      "POS, tips, and split checks wired to major restaurant systems.",
      "Kitchen display routing and prep timers for the line.",
      "Loyalty, multi-location menus, and ops analytics for groups.",
    ]
  ),
  "telecom-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Telecom apps at the intersection of scale and legacy.",
    "Customer self-service, field-ops, and BSS/OSS-facing platforms engineered for reliability, offline field work, and complex network integrations.",
    [
      "Self-service billing, plans, and support that deflect calls.",
      "Offline-capable technician apps for installs and repairs.",
      "BSS/OSS, CRM, billing, and inventory adapters.",
      "IoT connectivity portals and ops dashboards for carriers and MVNOs.",
    ]
  ),
  "accounting-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Accounting software is trust software.",
    "Bookkeeping, invoicing, reporting, and firm portals with double-entry discipline, immutable audit trails, and bank/ERP integrations finance teams can rely on.",
    [
      "Double-entry ledgers, journals, and reconciliations.",
      "Invoicing, AR/AP, and close-ready financial reporting.",
      "Plaid, Stripe, QuickBooks, Xero, and NetSuite sync.",
      "Encryption, RBAC, and immutable audit history for financial data.",
    ]
  ),
  "childcare-app-development-company": showcase(
    EducationAppVisual,
    "What We Build",
    "Childcare apps that earn trust every pickup.",
    "Parent communication, attendance, billing, and classroom tools designed for busy teachers, clear for parents, and rigorous on authorized pickup and family privacy.",
    [
      "Daily reports, photos, and messaging parents actually open.",
      "Secure check-in/out with authorized pickup lists.",
      "Tuition billing, autopay, and subsidy-aware flows.",
      "Allergy profiles, incident reports, and health/safety forms.",
    ]
  ),

  // ---------- BATCH 9 (locations) ----------
  "app-development-company-portland": locationShowcase(
    "app-development-company-portland",
    "On the Ground in Portland",
    "Portland teams fluent in cleantech and brand craft.",
    "From the Pearl District to Beaverton's tech corridor, we ship apps with the sustainability, ecommerce, and product-craft depth Portland companies expect.",
    [
      "On-site collaboration across Downtown, Pearl District, and Beaverton.",
      "Cleantech, climate, and sustainability product fluency.",
      "Brand-grade ecommerce and retail experience.",
      "Pacific Time alignment and startup-friendly velocity.",
    ]
  ),
  "app-development-company-minneapolis": locationShowcase(
    "app-development-company-minneapolis",
    "On the Ground in Minneapolis",
    "Twin Cities teams for health, retail, and enterprise.",
    "From Downtown Minneapolis to St. Paul and Bloomington, we ship apps with the HIPAA, retail, and Fortune-HQ integration depth the Twin Cities run on.",
    [
      "On-site collaboration across Downtown, Uptown, and St. Paul.",
      "Healthcare, medtech, and HIPAA-aware engineering.",
      "Retail and CPG commerce and loyalty fluency.",
      "Central Time alignment and enterprise delivery discipline.",
    ]
  ),
  "app-development-company-charlotte": locationShowcase(
    "app-development-company-charlotte",
    "On the Ground in Charlotte",
    "Charlotte teams fluent in banking and energy.",
    "As the second-largest U.S. banking center with a major energy footprint, Charlotte needs PCI-aware fintech engineering and utilities-grade ops apps, and we bring both.",
    [
      "On-site collaboration across Uptown, South End, and Ballantyne.",
      "Banking, fintech, and PCI/SOC 2–aware engineering.",
      "Energy and utilities customer and field-ops fluency.",
      "Eastern Time alignment and startup-friendly velocity.",
    ]
  ),

  // ---------- BATCH 10 ----------
  "java-app-development-company": showcase(
    CustomSoftwareHeroVisual,
    "What We Build",
    "Enterprise Java built for the next decade, not the next demo.",
    "Spring Boot services, high-throughput APIs, and modernized JVM platforms, typed, tested, and integrated with the ERPs and message buses your business already runs on.",
    [
      "Spring Boot 3 services on current Java LTS with clean layering.",
      "Kafka, JMS, and Spring Batch pipelines for reliable async work.",
      "Incremental Java 8 → 21 upgrades and monolith decomposition.",
      "SAP, Oracle, and MQ integrations with containerized cloud deploys.",
    ]
  ),
  "golang-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Go systems that stay fast and cheap to operate.",
    "Microservices, high-throughput APIs, and streaming pipelines in Go, profiled before launch, contract-first with gRPC, and running on a fraction of the compute other stacks need.",
    [
      "gRPC and REST services with protobuf contracts and buf tooling.",
      "Goroutine-based workers and Kafka/NATS consumers done safely.",
      "pprof profiling and load testing baked into every delivery.",
      "Static binaries and small K8s footprints that shrink cloud bills.",
    ]
  ),
  "api-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "APIs designed like products, because they are.",
    "Contract-first REST, GraphQL, and gRPC with the versioning, auth, and documentation that make your platform something partners integrate in days, not months.",
    [
      "OpenAPI, GraphQL SDL, and protobuf specs agreed before code.",
      "OAuth 2.1, scopes, rate limiting, and idempotency engineered in.",
      "Deprecation policies and sunset headers that never break consumers.",
      "Generated docs, SDKs, and sandboxes developers actually trust.",
    ]
  ),
  "home-services-app-development-company": showcase(
    OnDemandAppVisual,
    "What We Build",
    "Home services platforms that win in the driveway.",
    "Booking, dispatch, technician field apps, and payments for HVAC, plumbing, and cleaning operators, built for spotty signal, gloved hands, and homeowners who book in thirty seconds.",
    [
      "Online booking with real availability and arrival-window updates.",
      "Dispatch, live tech tracking, and route optimization per truck.",
      "Offline-capable field apps with job history, photos, and checklists.",
      "On-site quotes, card-on-file payments, and QuickBooks sync.",
    ]
  ),
  "church-app-development-company": showcase(
    MobileAppHeroVisual,
    "What We Build",
    "Church apps that connect the congregation all week long.",
    "Digital giving, sermon libraries, events, and small groups, warm, simple interfaces every generation in the pews can use, integrated with the ChMS your staff already runs.",
    [
      "One-tap recurring giving with campaigns and year-end statements.",
      "Live streaming plus sermon archives, series notes, and podcasts.",
      "Campus-aware events, volunteer signups, and push announcements.",
      "Planning Center and major ChMS integrations for clean member data.",
    ]
  ),
  "parking-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Parking platforms where every space earns.",
    "Driver reservations, contactless payments, enforcement tools, and garage ops dashboards, integrated with the gates, LPR cameras, and meters you already own.",
    [
      "Find, reserve, and pay flows with real-time availability.",
      "Pay-by-plate, QR entry, and validations that clear the exit lane.",
      "LPR enforcement, digital permits, and citation workflows.",
      "Occupancy heatmaps and dynamic pricing analytics for operators.",
    ]
  ),
  "news-app-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "News apps that turn readers into subscribers.",
    "Instant-loading feeds, metered paywalls, respectful push, and personalization, integrated with your CMS and engineered for election-night traffic spikes.",
    [
      "Offline-capable article feeds tuned for commutes and dead zones.",
      "Metered paywalls with StoreKit, Play Billing, and Stripe on web.",
      "Segmented breaking-news push with editorial tooling and quiet hours.",
      "WordPress, Arc XP, and custom CMS integration with entitlement sync.",
    ]
  ),
  "app-development-company-salt-lake-city": locationShowcase(
    "app-development-company-salt-lake-city",
    "On the Ground in Salt Lake City",
    "Salt Lake City teams that match Silicon Slopes velocity.",
    "From downtown SLC to the Lehi–Provo corridor, we ship apps with the SaaS, fintech, and health tech depth Utah's fastest-growing companies expect, on Mountain Time.",
    [
      "On-site collaboration across Downtown, Sugar House, and Lehi.",
      "Multi-tenant SaaS architecture at Silicon Slopes pace.",
      "Fintech and lending apps with PCI/SOC 2–aware engineering.",
      "Mountain Time alignment and startup-friendly velocity.",
    ]
  ),
  "app-development-company-raleigh": locationShowcase(
    "app-development-company-raleigh",
    "On the Ground in Raleigh",
    "Research Triangle teams fluent in science and software.",
    "From RTP's biotech cluster to a three-university edtech scene, we ship apps with the HIPAA, GxP, and enterprise integration depth the Triangle's research economy demands.",
    [
      "On-site collaboration across Downtown, North Hills, RTP, and Durham.",
      "Biotech and life sciences engineering with HIPAA/GxP awareness.",
      "Edtech and LMS-integrated products for a university-anchored region.",
      "Eastern Time alignment and enterprise delivery discipline.",
    ]
  ),
  "app-development-company-tampa": locationShowcase(
    "app-development-company-tampa",
    "On the Ground in Tampa Bay",
    "Tampa teams for finance, defense, and Gulf Coast trade.",
    "From the Westshore business district to MacDill's contractor ecosystem and Port Tampa Bay, we ship apps with the compliance and security depth the Bay's anchor industries run on.",
    [
      "On-site collaboration across Downtown, Westshore, and St. Pete.",
      "Fintech and insurtech with PCI/SOC 2–aware engineering.",
      "Security-first delivery for the defense-adjacent ecosystem.",
      "Eastern Time alignment and port-logistics fluency.",
    ]
  ),

  // ---------- BATCH 11 ----------
  "react-app-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "React web apps engineered for the next hundred releases.",
    "SPAs, dashboards, and design systems in TypeScript React, performance budgets, accessible components, and architecture separate from our React Native mobile practice.",
    [
      "TypeScript React with clean state, routing, and data-fetching layers.",
      "Design systems with Storybook, tokens, and a11y as defaults.",
      "Core Web Vitals budgets enforced during delivery, not after launch.",
      "Incremental migrations off jQuery, AngularJS, or untyped React.",
    ]
  ),
  "wordpress-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "WordPress that editors love and engineers can maintain.",
    "Custom themes, plugins, WooCommerce, and headless WordPress with React/Next frontends. Gutenberg blocks, SEO-safe migrations, and hardening that survives audits.",
    [
      "Custom Gutenberg blocks editors compose without breaking design.",
      "Headless WordPress + React/Next when app-grade UX is required.",
      "WooCommerce catalogs, subscriptions, and ERP/CRM integrations.",
      "Migrations that preserve rankings, redirects, and Core Web Vitals.",
    ]
  ),
  "shopify-app-development-company": showcase(
    EcommerceHeroVisual,
    "What We Build",
    "Shopify apps and storefronts past the theme ceiling.",
    "Custom apps, checkout extensions, and Hydrogen experiences. Plus-ready checkout logic and ERP/OMS sync so merchandising rules don't live in fragile theme hacks.",
    [
      "Public and custom Shopify apps on the GraphQL Admin API.",
      "Checkout UI extensions and Shopify Functions for Plus stores.",
      "Hydrogen/Oxygen storefronts when brand UX needs headless.",
      "ERP, OMS, and 3PL integrations designed before launch day.",
    ]
  ),
  "dental-app-development-company": showcase(
    HealthcareAppVisual,
    "What We Build",
    "Dental apps that fill chairs and calm the front desk.",
    "Patient booking, portals, treatment plans, and DSO ops tools. HIPAA-aware engineering integrated with the PMS and imaging systems practices already run.",
    [
      "Online booking, waitlists, and reminders that cut no-shows.",
      "Patient apps for forms, insurance cards, and treatment acceptance.",
      "Chairside treatment plans and imaging-adjacent clinical tools.",
      "Multi-location DSO reporting with consistent patient journeys.",
    ]
  ),
  "fleet-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Fleet platforms that survive the yard, not just the demo.",
    "Live tracking, exception-aware dispatch, offline driver apps, and telematics unification, built for rural dead zones, DVIR workflows, and cost-per-mile reality.",
    [
      "GPS tracking, geofences, and ETAs ops can act on immediately.",
      "Dispatch and routing that replans when the day goes sideways.",
      "Offline-capable driver apps for DVIR, stops, and messaging.",
      "Samsara, Geotab, Motive, and OEM telematics in one picture.",
    ]
  ),
  "loyalty-app-development-company": showcase(
    EcommerceHeroVisual,
    "What We Build",
    "Loyalty programs members open weekly, not delete after one scan.",
    "Points engines, VIP tiers, rewards catalogs, and referral flows connected to POS and ecommerce, with liability tracking finance can audit.",
    [
      "Earn/burn rules and tiers modeled for margin, not vanity.",
      "Member apps plus wallet passes for the lock-screen moment.",
      "Shopify, Square, Toast, and custom POS earn-event sync.",
      "Cohort retention, breakage, and LTV analytics for marketers.",
    ]
  ),
  "podcast-app-development-company": showcase(
    MobileAppHeroVisual,
    "What We Build",
    "Podcast apps that turn listening into a weekly habit.",
    "Offline-first listener apps, private/subscription feeds, and creator studios, chapters, car/Bluetooth continuity, and sponsor-grade completion analytics.",
    [
      "Offline downloads, chapters, speed, and sleep timers done right.",
      "Entitled private feeds and in-app or web subscription billing.",
      "Creator studio tools for upload, scheduling, and shownotes.",
      "Completion and cohort analytics sponsors and members trust.",
    ]
  ),
  "app-development-company-detroit": locationShowcase(
    "app-development-company-detroit",
    "On the Ground in Detroit",
    "Detroit teams for auto, manufacturing, and Motown startups.",
    "From Downtown and Midtown to Dearborn and Ann Arbor, we ship apps with the automotive, manufacturing, and fintech depth Metro Detroit's builders expect, on Eastern Time.",
    [
      "On-site collaboration across Downtown, Midtown, and Corktown.",
      "Connected vehicle, dealer, and mobility product fluency.",
      "Plant-ops and supply-chain apps for advanced manufacturers.",
      "Eastern Time alignment and startup-friendly velocity.",
    ]
  ),
  "app-development-company-orlando": locationShowcase(
    "app-development-company-orlando",
    "On the Ground in Orlando",
    "Orlando teams for tourism, healthcare, and aerospace.",
    "From Downtown and International Drive to Lake Nona's medtech cluster, we ship apps with guest-experience scale and the compliance depth Central Florida hospitals and aerospace suppliers require.",
    [
      "On-site collaboration across Downtown, Lake Nona, and Winter Park.",
      "Ticketing and guest apps tuned for peak-season traffic.",
      "HIPAA-aware engineering for Lake Nona health innovators.",
      "Security-first delivery for aerospace and defense-adjacent work.",
    ]
  ),
  "app-development-company-columbus": locationShowcase(
    "app-development-company-columbus",
    "On the Ground in Columbus",
    "Columbus teams for insurance, retail HQs, and logistics.",
    "From Downtown and the Short North to Dublin and Easton, we ship apps with the insurance, retail headquarters, and distribution depth Central Ohio's enterprise economy runs on.",
    [
      "On-site collaboration across Downtown, Short North, and Dublin.",
      "Policy, claims, and agent apps for Columbus insurers.",
      "Customer and associate tools for national retail HQs.",
      "Fleet and distribution fluency for Central Ohio logistics.",
    ]
  ),
  // ---------- BATCH 12 ----------
  "angular-app-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "Angular platforms built for the next decade of releases.",
    "Enterprise SPAs, design systems, and data-dense dashboards in typed Angular, module boundaries, RxJS discipline, and SSO patterns your internal team can own.",
    [
      "Lazy modules, strict TypeScript, and clean routing at scale.",
      "Design systems with Storybook, tokens, and a11y as defaults.",
      "OnPush performance budgets enforced during delivery.",
      "AngularJS and legacy Angular upgrades without freezing the roadmap.",
    ]
  ),
  "vue-app-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "Vue and Nuxt apps that stay fast as the product grows.",
    "Composition API SPAs, Nuxt SSR sites, and dashboards in TypeScript Vue, clear stores, design systems, and Core Web Vitals treated as product features.",
    [
      "Vue 3 Composition API with typed stores and routing.",
      "Nuxt SSR/hybrid rendering when SEO and speed demand it.",
      "Design systems with Storybook and accessible components.",
      "Incremental Vue 2 migrations that keep shipping features.",
    ]
  ),
  "ionic-app-development-company": showcase(
    HybridAppHeroVisual,
    "What We Build",
    "Ionic + Capacitor apps that feel intentional on every device.",
    "Hybrid iOS, Android, and PWA products from one web codebase, native plugins, offline sync, and store-ready builds without three separate frontends.",
    [
      "Capacitor plugins for camera, push, biometrics, and secure storage.",
      "Angular, React, or Vue frontends matched to your team.",
      "Offline-tolerant field flows that sync when coverage returns.",
      "Cordova-to-Capacitor migrations that protect store presence.",
    ]
  ),
  "property-management-app-development-company": showcase(
    RealEstateAppVisual,
    "What We Build",
    "Property ops apps that keep units, vendors, and residents aligned.",
    "Resident portals, work orders, leasing pipelines, and HOA tools, built for apartment operators and landlords, not listing marketplaces.",
    [
      "Resident apps for rent pay, requests, and amenity booking.",
      "Maintenance work orders with vendor SLAs and photo proof.",
      "Leasing and application flows that cut vacant days.",
      "Multi-property admin with occupancy and delinquency views.",
    ]
  ),
  "warehouse-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Warehouse apps the floor trusts mid-shift, not just in demos.",
    "Directed picking, receiving, cycle counts, and dock ops on scanner-first handhelds. ERP sync and accuracy metrics supervisors actually use.",
    [
      "Wave/zone picking and pack verification that cut mis-ships.",
      "Receiving, putaway, and location inventory ops can trust.",
      "Rugged handheld UX for gloves, glare, and dead Wi-Fi zones.",
      "ERP and carrier integrations without double entry.",
    ]
  ),
  "crm-app-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "Custom CRM shaped around how your revenue team sells.",
    "Pipelines, CPQ, customer 360, and RevOps automations, when off-the-shelf CRM force-fits stop matching your process.",
    [
      "Opportunity stages modeled on your real sales motion.",
      "Quotes, approvals, and contract handoffs without spreadsheet chaos.",
      "ERP, billing, and marketing sync that keeps one customer truth.",
      "Forecast and retention analytics leadership can defend.",
    ]
  ),
  "booking-app-development-company": showcase(
    OnDemandAppVisual,
    "What We Build",
    "Booking platforms that fill books and protect high-value slots.",
    "Appointments, reservations, deposits, and staff/resource calendars, reminders and waitlists that cut no-shows for services businesses.",
    [
      "Real-time availability with buffers and multi-resource rules.",
      "Deposits, card-on-file, and cancellation policies customers understand.",
      "SMS/email reminders and waitlists that recover idle capacity.",
      "Multi-location booking with utilization analytics operators open daily.",
    ]
  ),
  "app-development-company-indianapolis": locationShowcase(
    "app-development-company-indianapolis",
    "On the Ground in Indianapolis",
    "Indy teams for logistics, life sciences, and sports.",
    "From Downtown and Mass Ave to Broad Ripple, Carmel, and Fishers, we ship apps with the distribution, life sciences, and venue depth Central Indiana runs on.",
    [
      "On-site collaboration across Downtown, Broad Ripple, and Carmel.",
      "Warehouse, fleet, and supply-chain fluency for Indy's logistics corridor.",
      "HIPAA-aware builds for life sciences and health innovators.",
      "Event-scale guest tools for a sports-first city calendar.",
    ]
  ),
  "app-development-company-pittsburgh": locationShowcase(
    "app-development-company-pittsburgh",
    "On the Ground in Pittsburgh",
    "Pittsburgh teams for robotics, healthcare, and fintech.",
    "From Downtown and Oakland to the Strip District and Lawrenceville, we ship apps with the hard-tech, clinical, and university-spinout depth Western PA expects.",
    [
      "On-site collaboration across Downtown, Oakland, and South Side.",
      "Robotics ops and autonomy-adjacent product fluency.",
      "HIPAA-aware engineering for Western PA health systems.",
      "MVP discipline for CMU and Pitt research spinouts.",
    ]
  ),
  "app-development-company-kansas-city": locationShowcase(
    "app-development-company-kansas-city",
    "On the Ground in Kansas City",
    "KC teams for logistics, animal health, and fintech.",
    "From Downtown and the Crossroads to the Plaza and Overland Park, we ship apps with the distribution, animal-health, and startup depth the MO/KS metro runs on.",
    [
      "On-site collaboration across Downtown, Crossroads, and Overland Park.",
      "Fleet and warehouse fluency for KC's logistics economy.",
      "Veterinary and animal-health workflows built for the field.",
      "Central Time alignment and startup-friendly velocity.",
    ]
  ),
  // ---------- BATCH 13 ----------
  "typescript-app-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "TypeScript products where types mirror the domain.",
    "Typed React, Next.js, and Node apps with strict contracts, shared packages, and CI that refuses broken APIs, refactors stay cheap as the product grows.",
    [
      "Strict TypeScript across web, Node, and shared packages.",
      "Monorepos with typed UI kits and OpenAPI/tRPC contracts.",
      "Incremental JS→TS migrations that keep shipping features.",
      "Build pipelines and type-checks that catch breakages early.",
    ]
  ),
  "graphql-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "GraphQL APIs that stay fast as clients multiply.",
    "Schemas, resolvers, federation, and typed clients (DataLoaders, complexity budgets, and codegen so product teams iterate without N+1 chaos).",
    [
      "Domain-modeled schemas with safe deprecation paths.",
      "N+1-safe resolvers, batching, and query complexity limits.",
      "Federation/gateway patterns for multi-team ownership.",
      "TypeScript clients with codegen and disciplined caching.",
    ]
  ),
  "firebase-app-development-company": showcase(
    HybridAppHeroVisual,
    "What We Build",
    "Firebase apps that ship MVPs without painting you into a corner.",
    "Auth, Firestore, Cloud Functions, and FCM for mobile and web, security rules first, data models that age well, and clear exit ramps when scale demands more.",
    [
      "Auth, Firestore, and FCM wired for iOS, Android, and web.",
      "Security rules and custom claims treated as production code.",
      "Cloud Functions for payments, webhooks, and fan-out jobs.",
      "Growth paths to custom APIs when Firebase isn't the whole answer.",
    ]
  ),
  "inventory-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Inventory apps that answer one question: what do we actually have?",
    "SKU truth, multi-location counts, reorder alerts, and transfers, stock management for retail, manufacturing, and multi-site ops without a full WMS.",
    [
      "Item masters, lots/serials, and on-hand truth across locations.",
      "Mobile cycle counts with variance workflows ops trust.",
      "Reorder and low-stock alerts tied to real demand patterns.",
      "ERP and POS sync that ends spreadsheet reconciliation.",
    ]
  ),
  "delivery-app-development-company": showcase(
    OnDemandAppVisual,
    "What We Build",
    "Courier apps where dispatch and proof of delivery both hold.",
    "Last-mile dispatch, driver apps, live tracking, and POD, built for parcels and same-day networks, not restaurant batches or ride matching.",
    [
      "Zone-aware dispatch and multi-stop route assignment.",
      "Courier apps with exceptions, navigation, and earnings views.",
      "Photo, signature, and geofenced proof of delivery.",
      "SLA dashboards for on-time % and attempt rates.",
    ]
  ),
  "clinic-app-development-company": showcase(
    HealthcareAppVisual,
    "What We Build",
    "Clinic apps that keep the front desk and care team in one flow.",
    "Outpatient scheduling, digital intake, care-team tasking, and billing hooks, practice ops for clinics, not a full telemedicine megaplatform.",
    [
      "Multi-provider calendars with rooms, buffers, and no-show tools.",
      "Pre-visit intake, consents, and insurance capture on mobile.",
      "Care-team tasking that replaces hallway sticky notes.",
      "HIPAA-aware portals with EHR integration discipline.",
    ]
  ),
  "fantasy-sports-app-development-company": showcase(
    MobileAppHeroVisual,
    "What We Build",
    "Fantasy sports apps built for draft night and Sunday traffic.",
    "Season-long leagues, contest engines, live scoring, and social loops, scoring you can trust and infrastructure that survives kickoff spikes.",
    [
      "Commissioner tools for drafts, waivers, trades, and disputes.",
      "Live scoring pipelines with lag monitoring on game day.",
      "Contests, leaderboards, and engagement push between games.",
      "Load-tested architecture for draft night and peak Sundays.",
    ]
  ),
  "app-development-company-cleveland": locationShowcase(
    "app-development-company-cleveland",
    "On the Ground in Cleveland",
    "Cleveland teams for healthcare, manufacturing, and fintech.",
    "From Downtown and University Circle to Ohio City, Beachwood, and Lakewood, we ship apps with the clinical, industrial, and finance depth Northeast Ohio runs on.",
    [
      "On-site collaboration across Downtown, University Circle, and Beachwood.",
      "HIPAA-aware builds for NEO health systems and digital health.",
      "Manufacturing and supplier tools for Cleveland's industrial base.",
      "Eastern Time alignment and fintech-aware delivery.",
    ]
  ),
  "app-development-company-milwaukee": locationShowcase(
    "app-development-company-milwaukee",
    "On the Ground in Milwaukee",
    "Milwaukee teams for manufacturing, healthcare, and fintech.",
    "From Downtown and the Third Ward to Bay View, Wauwatosa, and Brookfield, we ship apps with the industrial, clinical, and finance depth Southeast Wisconsin expects.",
    [
      "On-site collaboration across Downtown, Third Ward, and Wauwatosa.",
      "Plant-adjacent manufacturing and supplier app fluency.",
      "HIPAA-aware engineering for SE Wisconsin health systems.",
      "Central Time alignment and compliance-minded fintech builds.",
    ]
  ),
  "app-development-company-sacramento": locationShowcase(
    "app-development-company-sacramento",
    "On the Ground in Sacramento",
    "Sacramento teams for government, healthcare, and agtech.",
    "From Downtown and Midtown to Natomas, Roseville, and Folsom, we ship apps with the civic, clinical, and Central Valley agtech depth the Capital Region runs on.",
    [
      "On-site collaboration across Downtown, Midtown, and Roseville.",
      "Accessible, audit-minded government and civic products.",
      "HIPAA-aware builds for Capital Region health systems.",
      "Field-ready agtech for Central Valley food and farm innovators.",
    ]
  ),
  // ---------- BATCH 14 ----------
  "ruby-on-rails-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "Rails products where conventions serve the domain.",
    "SaaS backends, APIs, and admin platforms in Ruby on Rails. Hotwire or API-first, PostgreSQL-first modeling, and delivery your engineers can own.",
    [
      "Multi-tenant SaaS patterns with roles and billing hooks.",
      "API-first Rails with serializers, auth, and versioning.",
      "Hotwire/Stimulus UIs when server-driven speed wins.",
      "Upgrades and modernization that keep shipping features.",
    ]
  ),
  "php-app-development-company": showcase(
    CustomSoftwareHeroVisual,
    "What We Build",
    "PHP apps that modernize without freezing the roadmap.",
    "Laravel APIs, custom backends, and legacy PHP modernization, secure auth, clear data layers, and CI/CD that makes releases boring again.",
    [
      "Laravel-first apps with queues, policies, and tests.",
      "Custom PHP APIs for web and mobile clients.",
      "Incremental legacy modernization without big-bang rewrites.",
      "Deploy pipelines on Forge, Vapor, or AWS.",
    ]
  ),
  "aws-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "AWS cloud-native apps with cost and ownership built in.",
    "Lambda, ECS, managed data, and IaC (workload-fit architecture, least-privilege IAM, and observability that keeps invoices and incidents explainable).",
    [
      "Serverless and container patterns matched to traffic shape.",
      "RDS, DynamoDB, Aurora, and S3 designed for access patterns.",
      "IAM, secrets, and logging that survive audits.",
      "Terraform/CDK pipelines your team can operate.",
    ]
  ),
  "loan-app-development-company": showcase(
    FintechAppVisual,
    "What We Build",
    "Lending apps from application to servicing, one loan truth.",
    "Consumer and SMB origination, underwriting workbenches, e-sign, and servicing tools, credit lifecycle software distinct from full banking cores.",
    [
      "Digital origination with document upload and status.",
      "KYC, soft pulls, and underwriting queues lenders trust.",
      "Offers, e-sign, and disbursement partner hooks.",
      "Servicing tools with audit trails for exams.",
    ]
  ),
  "pharmacy-app-development-company": showcase(
    HealthcareAppVisual,
    "What We Build",
    "Pharmacy apps patients finish and pharmacists can fulfill.",
    "Refill, adherence, delivery, and pharmacy work queues for retail and digital pharmacies, distinct from pharmaceutical R&D platforms.",
    [
      "Refill and transfer journeys that cut counter phone-tag.",
      "Adherence reminders and sync-fill programs that stick.",
      "Delivery and curbside status tied to real Rx readiness.",
      "HIPAA-aware PMS integrations and pharmacist queues.",
    ]
  ),
  "pet-app-development-company": showcase(
    OnDemandAppVisual,
    "What We Build",
    "Pet care apps that earn trust on every walk and booking.",
    "Walking, sitting, boarding, health logs, and marketplaces, consumer pet products distinct from veterinary clinic software.",
    [
      "Pet profiles, health logs, and care notes families share.",
      "Booking for walks, sits, and boarding with clear policies.",
      "Live tracking, photo proof, and caregiver payouts.",
      "Marketplace trust-and-safety for two-sided services.",
    ]
  ),
  "membership-app-development-company": showcase(
    HybridAppHeroVisual,
    "What We Build",
    "Membership apps that prove value between bill cycles.",
    "Dues billing, access control, classes/events, and member portals for gyms, clubs, and associations, retention-minded products ops can run.",
    [
      "Plans, proration, and failed-payment recovery.",
      "QR/NFC check-in tied to active membership status.",
      "Class and event booking members actually use.",
      "Retention analytics leadership can act on weekly.",
    ]
  ),
  "app-development-company-st-louis": locationShowcase(
    "app-development-company-st-louis",
    "On the Ground in St. Louis",
    "St. Louis teams for healthcare, logistics, and fintech.",
    "From Downtown and Cortex to Clayton, Central West End, and Soulard, we ship apps with the clinical, freight, and finance depth the metro runs on.",
    [
      "On-site collaboration across Downtown, Cortex, and Clayton.",
      "HIPAA-aware builds for St. Louis health systems.",
      "Logistics tools for the Mississippi corridor.",
      "Central Time alignment and manufacturing-aware delivery.",
    ]
  ),
  "app-development-company-cincinnati": locationShowcase(
    "app-development-company-cincinnati",
    "On the Ground in Cincinnati",
    "Cincinnati teams for CPG, healthcare, and insurance.",
    "From Downtown and Over-the-Rhine to Covington, Hyde Park, and Blue Ash, we ship apps with the brand, clinical, and insurance depth the tri-state expects.",
    [
      "On-site collaboration across Downtown, OTR, and Hyde Park.",
      "CPG and consumer brand app fluency.",
      "HIPAA-aware engineering for tri-state health systems.",
      "Eastern Time alignment and insurance-aware delivery.",
    ]
  ),
  "app-development-company-jacksonville": locationShowcase(
    "app-development-company-jacksonville",
    "On the Ground in Jacksonville",
    "Jacksonville teams for logistics, finance, and healthcare.",
    "From Downtown and Riverside to the Beaches, Southside, and Town Center, we ship apps with the freight, finance, and clinical depth Northeast Florida runs on.",
    [
      "On-site collaboration across Downtown, Riverside, and Beaches.",
      "Logistics and port-adjacent ops fluency.",
      "Compliance-minded fintech and HIPAA-aware healthcare.",
      "Eastern Time alignment and defense-adjacent security habits.",
    ]
  ),
  // ---------- BATCH 15 ----------
  "azure-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Azure cloud-native apps with identity and cost built in.",
    "App Service, AKS, Functions, and managed data (Entra ID, Key Vault, and IaC that keep invoices and audits explainable for Microsoft-standardized teams).",
    [
      "App Service and API platforms with slots and auth.",
      "AKS containers with sensible networking and GitOps.",
      "Azure SQL, Cosmos, and Storage matched to access patterns.",
      "Bicep/Terraform pipelines your engineers can operate.",
    ]
  ),
  "kubernetes-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Kubernetes products where platforms serve shipping.",
    "Containerized APIs, workers, and GitOps delivery on AKS, EKS, or GKE, probes, limits, observability, and ownership your team can run.",
    [
      "Twelve-factor services packaged for real cluster life.",
      "Managed clusters with ingress and environment separation.",
      "Helm/GitOps pipelines that make rollbacks boring.",
      "RBAC, secrets, and metrics SRE and product share.",
    ]
  ),
  "capacitor-app-development-company": showcase(
    HybridAppHeroVisual,
    "What We Build",
    "Capacitor hybrids that let web teams own the stores.",
    "One React, Vue, or Angular codebase to iOS, Android, and web, native plugins, offline habits, and store packaging distinct from Ionic-only UI kits.",
    [
      "Store builds from your existing web product core.",
      "Camera, push, biometrics, and custom native bridges.",
      "Auth, secure storage, and deep links that survive OS updates.",
      "CI/CD for TestFlight, Play Console, and web deploys.",
    ]
  ),
  "cleaning-app-development-company": showcase(
    OnDemandAppVisual,
    "What We Build",
    "Cleaning apps where booking and crew truth match.",
    "Residential maid, commercial janitorial, and facility cleaning, schedules, dispatch, checklists, and quality photos operators can run.",
    [
      "Recurring booking with add-ons and clear pricing.",
      "Crew dispatch and route boards with honest ETAs.",
      "Room checklists and photo proof that close disputes.",
      "Packages, tips, and margin dashboards for ops leaders.",
    ]
  ),
  "moving-app-development-company": showcase(
    OnDemandAppVisual,
    "What We Build",
    "Moving apps that survive Saturday dispatch chaos.",
    "Local moves, labor marketplaces, and relocation flows, digital quotes, crew boards, inventory checklists, and settlement in one journey.",
    [
      "Inventories and photo quotes that cut day-of surprises.",
      "Labor matching with ratings and clear job scopes.",
      "Truck and crew dispatch with live customer ETAs.",
      "Deposits, balances, tips, and payouts settled cleanly.",
    ]
  ),
  "investment-app-development-company": showcase(
    FintechAppVisual,
    "What We Build",
    "Investment apps where clarity beats spectacle.",
    "Brokerage, wealth, and portfolio products (onboarding, watchlists, orders or advice, and performance views distinct from banking cores and crypto-only wallets).",
    [
      "Portfolio and performance dashboards investors trust.",
      "Watchlists, alerts, and order entry with real status.",
      "KYC, suitability, and disclosure audit trails.",
      "Advisor and client roles with funding and statements.",
    ]
  ),
  "ticketing-app-development-company": showcase(
    TravelAppVisual,
    "What We Build",
    "Ticketing where inventory and the door share truth.",
    "Event and venue ticket sales, transfers, and mobile scanning, inventory integrity and access control distinct from general event marketing apps.",
    [
      "Sections, holds, comps, and price tiers promoters use.",
      "Checkout with fees, promos, and abandoned-cart recovery.",
      "Offline-capable scanning with duplicate detection.",
      "Sell-through and scan analytics before the next on-sale.",
    ]
  ),
  "app-development-company-baltimore": locationShowcase(
    "app-development-company-baltimore",
    "On the Ground in Baltimore",
    "Baltimore teams for healthcare, biotech, and logistics.",
    "From Downtown and Inner Harbor to Harbor East, Fells Point, Canton, and Columbia, we ship apps with the clinical, research, and port depth Central Maryland runs on.",
    [
      "On-site collaboration across Downtown, Harbor East, and Canton.",
      "HIPAA-aware builds for Baltimore health systems.",
      "Biotech and research-adjacent ops fluency.",
      "Eastern Time alignment and port logistics awareness.",
    ]
  ),
  "app-development-company-louisville": locationShowcase(
    "app-development-company-louisville",
    "On the Ground in Louisville",
    "Louisville teams for healthcare, logistics, and manufacturing.",
    "From Downtown and NuLu to the Highlands, East End, St. Matthews, and Jeffersontown, we ship apps with the clinical, freight, and industrial depth Kentuckiana expects.",
    [
      "On-site collaboration across Downtown, NuLu, and East End.",
      "HIPAA-aware engineering for Louisville health systems.",
      "Distribution and hub logistics fluency.",
      "Eastern Time alignment and manufacturing-aware delivery.",
    ]
  ),
  "app-development-company-oklahoma-city": locationShowcase(
    "app-development-company-oklahoma-city",
    "On the Ground in Oklahoma City",
    "OKC teams for energy, aerospace, and healthcare.",
    "From Downtown and Midtown to Bricktown, Quail Creek, Edmond, and Norman, we ship apps with the field, flight, and clinical depth the OKC metro runs on.",
    [
      "On-site collaboration across Downtown, Midtown, and Edmond.",
      "Offline-aware energy and field ops fluency.",
      "Aerospace and audit-minded security habits.",
      "Central Time alignment with healthcare and fintech depth.",
    ]
  ),
  // ---------- BATCH 16 ----------
  "machine-learning-app-development-company": showcase(
    AiMlHeroVisual,
    "What We Build",
    "Production ML where models serve product outcomes.",
    "Feature pipelines, eval suites, and inference APIs (ranking, forecasting, and anomaly surfaces distinct from generative-AI chat overlays).",
    [
      "Problem framing and success metrics before model spend.",
      "Feature contracts that reduce offline/online skew.",
      "Serving, monitoring, and rollbacks ops can own.",
      "ML-powered UX users understand and trust.",
    ]
  ),
  "devops-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "DevOps platforms that make shipping boring, in a good way.",
    "CI/CD, IaC, observability, and release guardrails so product teams deploy weekly without weekend fire drills.",
    [
      "Pipelines with gates that catch regressions early.",
      "Terraform/Bicep environments you can recreate.",
      "Shared metrics, logs, and traces for incidents.",
      "Canaries and rollbacks that keep Friday safe.",
    ]
  ),
  "dedicated-development-team": showcase(
    CustomSoftwareHeroVisual,
    "What We Build",
    "Dedicated senior pods embedded in your roadmap.",
    "Staff augmentation that owns outcomes (your Slack, your standups, your backlog) with a path to Maxiom Developers for staffing-first hiring.",
    [
      "2–8 senior engineers matched to stack and domain.",
      "Delivery habits that keep velocity honest.",
      "Docs and pairing so knowledge stays with you.",
      "Ramp in weeks, not a recruiting quarter.",
    ]
  ),
  "fractional-cto-services": showcase(
    CustomSoftwareHeroVisual,
    "What We Build",
    "Fractional CTO leadership that actually operates.",
    "Architecture, hiring plans, and delivery cadence, routed to CTO 2.0 when executive leadership is the primary need.",
    [
      "Stack and architecture decisions matched to stage.",
      "Interview loops that raise the hiring bar.",
      "Sprint rituals that restore roadmap trust.",
      "Interim cover through fundraising or transitions.",
    ]
  ),
  "ehr-app-development-company": showcase(
    HealthcareAppVisual,
    "What We Build",
    "EHR modules clinicians finish faster.",
    "Charting workflows, FHIR integrations, and specialty templates, healthcare IT depth with Maxiom Labs when you need broader clinical partnership.",
    [
      "Encounter flows tuned for charting speed.",
      "FHIR and partner exchange that survive IT review.",
      "HIPAA-aware access and audit trails.",
      "Provider and patient surfaces on one clinical truth.",
    ]
  ),
  "supply-chain-app-development-company": showcase(
    CommerceOpsDashboardVisual,
    "What We Build",
    "Supply chain apps where every node shares truth.",
    "Multi-tier inventory, ASN/PO visibility, and partner portals, distinct from single-site WMS or last-mile delivery apps.",
    [
      "On-hand and in-transit inventory across nodes.",
      "Supplier portals and scorecards that get used.",
      "Exception workflows with clear owners.",
      "ERP/WMS/TMS integrations that respect master data.",
    ]
  ),
  "cybersecurity-app-development-company": showcase(
    PlatformOpsDashboardVisual,
    "What We Build",
    "Security products where trust is engineered.",
    "Analyst consoles, customer portals, and compliance evidence modules, multi-tenant auth and audit trails from day one.",
    [
      "Case workflows tuned for SOC pace.",
      "Customer trust portals that cut ticket ping-pong.",
      "RBAC, SSO, and tenant isolation for enterprise buyers.",
      "Evidence packages auditors can export.",
    ]
  ),
  "app-development-company-las-vegas": locationShowcase(
    "app-development-company-las-vegas",
    "On the Ground in Las Vegas",
    "Las Vegas teams for hospitality, venues, and logistics.",
    "From the Strip and Downtown to Summerlin and Henderson, we ship apps with the guest, staff, and peak-night depth Southern Nevada expects.",
    [
      "On-site collaboration across Strip, Downtown, and Summerlin.",
      "Hospitality and venue ops fluency.",
      "Ticketing and experience product patterns.",
      "Pacific Time alignment with logistics awareness.",
    ]
  ),
  "app-development-company-san-antonio": locationShowcase(
    "app-development-company-san-antonio",
    "On the Ground in San Antonio",
    "San Antonio teams for healthcare, cyber, and mission work.",
    "From Downtown and the Pearl to Stone Oak and Southtown, we ship apps with the clinical, security, and defense-adjacent depth South Texas runs on.",
    [
      "On-site collaboration across Downtown, Pearl, and Stone Oak.",
      "HIPAA-aware healthcare engineering.",
      "Cybersecurity product and portal fluency.",
      "Central Time alignment with mission-minded security habits.",
    ]
  ),
  "app-development-company-richmond": locationShowcase(
    "app-development-company-richmond",
    "On the Ground in Richmond VA",
    "Richmond teams for finance, healthcare, and government-adjacent work.",
    "From Downtown and Scott's Addition to Short Pump and the Fan, we ship apps with the finance, clinical, and capital-corridor depth Central Virginia expects, with DMV continuity.",
    [
      "On-site collaboration across Downtown, Scott's Addition, and Short Pump.",
      "Finance and insurance compliance fluency.",
      "HIPAA-aware healthcare engineering.",
      "Eastern Time alignment with Virginia and DC continuity.",
    ]
  ),
  // ---------- BATCH 17 ----------
  "computer-vision-app-development-company": showcase(
    AiMlHeroVisual,
    "What We Build",
    "Computer vision that survives the real world, not just the demo.",
    "Detection, OCR, and inspection models wired into apps that hold accuracy under motion blur, bad lighting, and edge cases, on device or in the cloud.",
    [
      "Detection, OCR, segmentation, and tracking pipelines.",
      "On-device inference with Core ML, TF Lite, and ONNX.",
      "Labeling and active learning that keep improving models.",
      "Eval suites and drift monitoring precision teams trust.",
    ]
  ),
  "django-app-development-company": showcase(
    WebAppHeroVisual,
    "What We Build",
    "Django apps and APIs that reward discipline.",
    "Thin views, real test coverage, and query performance that holds as you grow. Django and DRF used the way the framework intends.",
    [
      "Django REST Framework and GraphQL APIs with docs.",
      "PostgreSQL modeling, migrations, and query tuning.",
      "Celery and Redis for async jobs and pipelines.",
      "Hardened auth, permissions, and audit trails.",
    ]
  ),
  "salesforce-app-development-company": showcase(
    CustomSoftwareHeroVisual,
    "What We Build",
    "Salesforce treated like engineering, not just clicks.",
    "Custom Lightning apps, bulk-safe Apex, and integrations that make Salesforce the source of truth, with Git, CI/CD, and test coverage.",
    [
      "Lightning Web Components and App Builder experiences.",
      "Apex and Flow that respect governor limits.",
      "Integrations with ERP, billing, and data platforms.",
      "AppExchange managed packages built for security review.",
    ]
  ),
  "elearning-app-development-company": showcase(
    EducationAppVisual,
    "What We Build",
    "Learning products engineered for completion.",
    "Courses, live cohorts, and assessments with the pacing, nudges, and analytics that move completion rates, not just signups.",
    [
      "SCORM, xAPI, and LTI-ready course delivery.",
      "Adaptive video, live classes, and offline lessons.",
      "Assessments, certificates, and competency tracking.",
      "Drop-off analytics that pinpoint what to fix.",
    ]
  ),
  "field-service-app-development-company": showcase(
    OnDemandAppVisual,
    "What We Build",
    "Field service apps built for the basement with no signal.",
    "Offline-first work orders, dispatch, and asset history, designed technician-first so adoption is high and data stays complete.",
    [
      "Offline work orders with photos and signatures.",
      "Scheduling, dispatch, and route optimization.",
      "Asset history, truck stock, and parts consumption.",
      "ERP, CRM, and accounting integrations that hold.",
    ]
  ),
  "video-streaming-app-development-company": showcase(
    MobileAppHeroVisual,
    "What We Build",
    "Streaming that starts instantly and never buffers at the worst moment.",
    "VOD, live, and OTT with adaptive playback, studio-grade DRM, and monetization, smooth from one viewer to millions.",
    [
      "Adaptive HLS/DASH with multi-CDN failover.",
      "Widevine, FairPlay, and PlayReady DRM.",
      "iOS, Android, web, and connected-TV apps.",
      "SVOD, TVOD, and AVOD with unified entitlements.",
    ]
  ),
  "car-rental-app-development-company": showcase(
    TravelAppVisual,
    "What We Build",
    "Car rental where availability is the product.",
    "Booking, fleet, and keyless pickup with real-time inventory integrity, so the right car is always where the customer expects it.",
    [
      "Real-time availability with no double-booking.",
      "Keyless unlock, telematics, and GPS integration.",
      "Deposits, damage capture, and insurance workflows.",
      "Dynamic pricing and revenue-per-vehicle analytics.",
    ]
  ),
  "app-development-company-new-orleans": locationShowcase(
    "app-development-company-new-orleans",
    "On the Ground in New Orleans",
    "New Orleans teams for energy, ports, and hospitality.",
    "From the CBD and French Quarter to Metairie, Kenner, and the North Shore, we ship apps with the energy, maritime, clinical, and guest-experience depth the Gulf Coast runs on.",
    [
      "On-site collaboration across the CBD, French Quarter, and Metairie.",
      "Offline-aware energy and maritime field ops fluency.",
      "HIPAA-aware healthcare engineering.",
      "Central Time alignment with port logistics awareness.",
    ]
  ),
  "app-development-company-omaha": locationShowcase(
    "app-development-company-omaha",
    "On the Ground in Omaha",
    "Omaha teams for finance, logistics, and agtech.",
    "From Downtown and the Old Market to Aksarben, West Omaha, and Council Bluffs, we ship apps with the finance, freight, and agriculture depth the Silicon Prairie expects.",
    [
      "On-site collaboration across Downtown, Old Market, and Aksarben.",
      "Finance and insurance compliance fluency.",
      "Rail, trucking, and warehouse logistics awareness.",
      "Central Time alignment with agtech supply-chain depth.",
    ]
  ),
  "app-development-company-hartford": locationShowcase(
    "app-development-company-hartford",
    "On the Ground in Hartford",
    "Hartford teams for insurance, finance, and manufacturing.",
    "From Downtown and Asylum Hill to West Hartford, Farmington, and Bloomfield, we ship apps with the insurance, financial, clinical, and precision-manufacturing depth Central Connecticut runs on.",
    [
      "On-site collaboration across Downtown, Asylum Hill, and West Hartford.",
      "Insurance, claims, and underwriting fluency.",
      "Finance and wealth-management compliance depth.",
      "Eastern Time alignment with healthcare and manufacturing.",
    ]
  ),
};

export function getLandingShowcase(slug) {
  return landingShowcases[slug] || null;
}
